// MACHINERY V2 (Stroje) — Servisné správy: the status chips + sortable column
// header (M2.5).
//
// Split out of ServiceReportsTabV2.tsx for the 300-line cap. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`PtFilterChip`,
// `SrHead`); artboard 1000.
//
// THE CHIP COUNTS ARE EXACT, NOT ESTIMATED. They are derived by the tab from the
// full result set — see the "status filtering is client-side" block in
// ServiceReportsTabV2.tsx for why every report is in hand.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ServiceReportStatusCode } from "../../../models/ServiceReportsModel";
import ChipScroller from "../../mobile/ChipScroller";
import { SortGlyph, TabIntro, TabSearch, headCellStyle } from "./tabAtoms";

/**
 * The status filter chip.
 *
 * >>> BELOW 1280 THE PAINTED PILL AND THE HIT TARGET ARE NOT THE SAME ELEMENT
 * (M2.16d review, F6). <<< The board's chip is 28px, which is a third of the
 * ">= 44px on BOTH axes" floor this module applies below 1280 — and the floor
 * used to be delivered by a CSS rule matching `button[aria-pressed]` inside the
 * profile, which GREW THE PAINT: a 28px pill became a 44px lozenge across the
 * whole 768-1279 tablet tier. That contradicts the principle this same commit
 * states next door — `partsAtoms.tsx` argues a 44px teal square "is not a
 * checkbox any more" and wraps a 16px paint in a transparent 44x44 button. Same
 * idiom here (and in `mobile/TouchChip.tsx`, where it is the module's
 * primitive): the BUTTON is transparent and >= 44px, a span paints the board's
 * 28px pill. The row looks like the board and touches like the standard.
 *
 * `touch` OMITTED IS TODAY'S DESKTOP ELEMENT, BYTE FOR BYTE — one button, the
 * same style object, no wrapper.
 *
 * It is a PROP, not a media query in this file: the tab reads the query once and
 * passes the boolean down, which is what useV2MediaQuery's header requires of a
 * component that renders three times per toolbar.
 */
const chipPill = (active: boolean): React.CSSProperties => ({
  height: 28,
  padding: "0 11px",
  borderRadius: 999,
  fontFamily: "inherit",
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
  // Required by ChipScroller's `flexWrap: nowrap` row (board 21): without
  // it the chips squash instead of scrolling. It is a no-op in the >= 768
  // toolbar, which is a WRAPPING row — a chip there wraps before it shrinks,
  // so no desktop computed box changes.
  flexShrink: 0,
  background: active ? "var(--mod-accent)" : "var(--card)",
  border: "1px solid " + (active ? "var(--mod-accent)" : "var(--border)"),
  color: active ? "var(--mod-accent-fg)" : "var(--ink-2)",
  fontWeight: active ? 600 : 400,
});

export const SrFilterChip: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  /** Below 1280 (`V2_BELOW_DESKTOP`) — wrap the pill in a legal touch target. */
  touch?: boolean;
}> = ({ label, count, active, onClick, touch }) => {
  const body = (
    <React.Fragment>
      {label}
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, opacity: 0.75 }}>
        {count}
      </span>
    </React.Fragment>
  );

  if (!touch) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        style={{ ...chipPill(active), cursor: "pointer" }}
      >
        {body}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        // 44px minimum on both axes, transparent — the span is what the user
        // sees. No `minWidth: 0` follows this pair; one would silently void it.
        minHeight: 44,
        minWidth: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        border: 0,
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span style={chipPill(active)}>{body}</span>
    </button>
  );
};

export const SrHeadCell: React.FC<{
  label: string;
  dir?: "asc" | "desc";
  align?: "right";
  onClick: () => void;
}> = ({ label, dir, align, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      ...headCellStyle,
      border: 0,
      background: "transparent",
      padding: 0,
      cursor: "pointer",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
    }}
  >
    {label}
    <SortGlyph dir={dir} />
  </button>
);

export interface SrToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  status: ServiceReportStatusCode | null;
  onStatusChange: (s: ServiceReportStatusCode | null) => void;
  counts: { all: number; inProgress: number; solved: number };
  visibleCount: number;
  /** Board 21's phone composition. Omitted = artboard 1000, unchanged. */
  phone?: boolean;
  /** `V2_BELOW_DESKTOP` — the whole 768-1279 tier paints this desktop toolbar on
   *  a touch device, so the status chips need a legal target there too, not only
   *  under 768. Omitted = the >= 1280 chip, unchanged. */
  touch?: boolean;
  /** Board 21's "+ Správa", the phone tab's own create entry. Only read when
   *  `phone` is set; at >= 768 the create action is the hero's. */
  onCreate?: () => void;
}

/**
 * Search + the three status chips + the mono count (artboard 1000, top row).
 *
 * BOARD 21 REORDERS THIS ON A PHONE and adds one control:
 *   row 1  the status chips in a horizontal SCROLLER, ending in "+ Správa"
 *   row 2  the search, full width
 * and it drops the mono result count, which is the least useful 10px of text on
 * a 390px screen and is restated by the list itself.
 *
 * WHY "+ Správa" EXISTS ONLY HERE. Below 1280 the hero collapses to board 20's
 * compact band (ProfileHeroPhone.tsx), which keeps the primary buttons — but the
 * board puts a create entry on the SR tab itself, where a user who came to read
 * reports already is. It calls the SAME `createReport` the hero and the empty
 * state call; there is no second create path.
 *
 * The scroller is the kit's ChipScroller: one fade, one hidden scrollbar, one
 * trailing spacer, module-wide. Its spacer is why the terminal "+ Správa" is not
 * rendered half-erased under the gradient — the exact defect that component's
 * header documents.
 */
export const SrToolbar: React.FC<SrToolbarProps> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  counts,
  visibleCount,
  phone,
  touch,
  onCreate,
}) => {
  const { translate } = useTranslation("myMachinery");

  const searchField = (
    <TabSearch
      value={search}
      onChange={onSearchChange}
      placeholder={translate("myMachinery:searchReports")}
      clearLabel={translate("myMachinery:v2.tabs.clearSearch")}
      fill={phone}
    />
  );
  const chips = (
    <React.Fragment>
      <SrFilterChip
        label={translate("common:all")}
        count={counts.all}
        active={status === null}
        onClick={() => onStatusChange(null)}
        touch={touch}
      />
      <SrFilterChip
        label={translate("myMachinery:serviceReportStatus.inProgress")}
        count={counts.inProgress}
        active={status === ServiceReportStatusCode.inProgress}
        onClick={() => onStatusChange(ServiceReportStatusCode.inProgress)}
        touch={touch}
      />
      <SrFilterChip
        label={translate("myMachinery:serviceReportStatus.solved")}
        count={counts.solved}
        active={status === ServiceReportStatusCode.solved}
        onClick={() => onStatusChange(ServiceReportStatusCode.solved)}
        touch={touch}
      />
    </React.Fragment>
  );

  if (phone) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <ChipScroller fadeTo="var(--bg)" gap={6} paddingInline={0}>
          {chips}
          {onCreate && (
            <button
              type="button"
              className="btn btn-mod"
              onClick={onCreate}
              // No `minWidth: 0` after this pair — one would void the floor.
              style={{ height: 44, minWidth: 44, flexShrink: 0, whiteSpace: "nowrap" }}
            >
              <span aria-hidden style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>
                +
              </span>
              {translate("myMachinery:v2.profile.newReport")}
            </button>
          )}
        </ChipScroller>
        {searchField}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
      {searchField}
      {chips}
      <span style={{ flex: 1 }} />
      <TabIntro count={translate("myMachinery:v2.tabs.sr.count", { count: visibleCount })} />
    </div>
  );
};
